import React from 'react';
import SmallDonutChartDetail from 'spw-components/charts/small-donut-chart-detail';
import {
  INDIVIDUAL_SPACES_CATEGORY,
  INDIVIDUAL_WORKSPACES_CATEGORY,
  SHARED_SPACES_CATEGORY,
  SHARED_SPACES_KEY,
} from 'spw-constants';
import {
  IProjectColors,
  IProjectSpaceTypeCategory,
} from 'spw-redux/modules/projects/types';
import { hasCustomSubcategory } from 'spw-redux/modules/project-space-type-categories/utils';
import { getColor } from 'spw-shared/utils';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { SummaryCalculator } from '../../../../models/ProgramSummaryCalculator';
import { PlainWorksheet } from '../../../../models/Worksheet';

interface SpaceAllocationChartProps {
  summaryCalculator: SummaryCalculator;
  worksheet: PlainWorksheet;
  isTotalProgramScenario: boolean;
  individualWorkspacesUsf: number;
  sharedSpacesUsf: number;
  detailedSharedConfig?: boolean;
  height?: string;
  projectColors: IProjectColors;
  totalProgramWorksheets?: PlainWorksheet[];
  individualWorkspacesCategory: IProjectSpaceTypeCategory;
}

const TotalProgramAllocationUsfChart: React.FunctionComponent<SpaceAllocationChartProps> = ({
  summaryCalculator,
  worksheet,
  individualWorkspacesUsf,
  sharedSpacesUsf,
  detailedSharedConfig,
  height,
  projectColors,
  totalProgramWorksheets,
  individualWorkspacesCategory,
  isTotalProgramScenario,
}) => {
  const { getUnit, convertArea } = useUnitConversion();

  const { subtotalProgramUSF } = worksheet;
  const {
    totalProgramUSF,
    circulationUSF,
    additionalUSFForPlanningFactor,
  } = summaryCalculator;

  const individualWorkspacesPercent = individualWorkspacesUsf / totalProgramUSF;
  const sharedSpacesPercent = sharedSpacesUsf / totalProgramUSF;
  const cirulationPercent = circulationUSF / totalProgramUSF;
  const planningFactorPercent =
    additionalUSFForPlanningFactor / totalProgramUSF;

  const data = [
    ['Space Type', 'Count'],
    ['Individual Workspaces', convertArea(individualWorkspacesUsf) || 0],
    ['Shared Spaces', convertArea(sharedSpacesUsf) || 0],
    ['Circulation', convertArea(circulationUSF) || 0],
    ['Planning Factor', convertArea(additionalUSFForPlanningFactor) || 0],
  ];

  const spacesData = [
    {
      quantity: convertArea(individualWorkspacesUsf),
      percent: subtotalProgramUSF ? individualWorkspacesPercent : 0,
      color: getColor(INDIVIDUAL_WORKSPACES_CATEGORY, projectColors),
      name: individualWorkspacesCategory.name,
      category: INDIVIDUAL_SPACES_CATEGORY,
    },
    {
      quantity: convertArea(sharedSpacesUsf),
      percent: subtotalProgramUSF ? sharedSpacesPercent : 0,
      color: projectColors[SHARED_SPACES_KEY] as string,
      name: 'Shared Spaces',
      category: SHARED_SPACES_CATEGORY,
    },
    {
      quantity: convertArea(circulationUSF),
      percent: cirulationPercent,
      color: '#A4A4A4',
      name: 'Circulation',
      category: 'Circulation-US98DjCk',
    },
    {
      quantity: convertArea(additionalUSFForPlanningFactor),
      percent: planningFactorPercent,
      color: '#808080',
      name: 'Planning Factor',
      category: 'Planning Factor-US98DjCk',
    },
  ];

  return (
    <div
      data-testid="total_program_allocation_usf_chart"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: height || 232,
      }}
    >
      <SmallDonutChartDetail
        forceDisableAllocationDialog
        showStackedNames
        isTotalProgramScenario={isTotalProgramScenario}
        totalProgramWorksheets={totalProgramWorksheets}
        style={{ height: height || 232 }}
        title="TOTAL PROGRAM ALLOCATION"
        subtitle={`Total Program ${getUnit('area')}`}
        total={convertArea(totalProgramUSF)}
        spacesData={spacesData}
        data={data}
        colors={[
          projectColors[INDIVIDUAL_WORKSPACES_CATEGORY] as string,
          projectColors[SHARED_SPACES_CATEGORY] as string,
          '#A4A4A4' /* for circulation */,
          '#808080' /* for planning factor */,
        ]}
        showNamesInTable={!detailedSharedConfig}
        spaceTypeNamesStyle={{
          paddingTop: hasCustomSubcategory(individualWorkspacesCategory)
            ? 26
            : 0,
        }}
        testingPrefix="programming_ps_totalprogramalloc_shared"
      />
    </div>
  );
};

export default React.memo(TotalProgramAllocationUsfChart);
